import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CampaignBuilderComplete from '@/components/CampaignBuilderComplete'
import { useAuth } from '@/contexts/AuthContext'
import { apiService } from '@/services/api'
import { campaignService } from '@/services/campaignService'

export default function MinimalCampaignBuilder() {
  const navigate = useNavigate()
  const { id: campaignId } = useParams()
  const { user } = useAuth()
  const [campaign, setCampaign] = useState<any>(null)
  const [assets, setAssets] = useState<any[]>([])
  const [brdRules, setBrdRules] = useState<any[]>([])
  const [traffickingHeaders, setTraffickingHeaders] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [loadingAssets, setLoadingAssets] = useState(true)

  // Load campaign data
  useEffect(() => {
    if (user && campaignId) {
      loadCampaign()
      loadCampaignAssets()
    }
    if (user) {
      loadUserData()
    }
  }, [user, campaignId])
  
  // Prevent double loading in React Strict Mode
  const [hasLoaded, setHasLoaded] = useState(false)

  const loadCampaign = async () => {
    if (!campaignId || hasLoaded) return
    
    try {
      const campaignData = await campaignService.getCampaign(campaignId)
      if (campaignData) {
        setCampaign(campaignData)
        setHasLoaded(true)
      }
    } catch (error) {
      console.error('Failed to load campaign:', error)
    }
  }
  
  const loadCampaignAssets = async () => {
    if (!campaignId) return
    
    setLoadingAssets(true)
    try {
      const campaignAssets = await campaignService.getCampaignAssets(campaignId)
      if (campaignAssets && campaignAssets.length > 0) {
        console.log(`✅ MinimalCampaignBuilder loaded ${campaignAssets.length} assets`, {
          firstAssetKeys: Object.keys(campaignAssets[0] || {}),
          hasUrl: !!campaignAssets[0]?.url,
          hasThumbnail: !!campaignAssets[0]?.thumbnailLink,
          sampleAsset: campaignAssets[0]
        })
        setAssets(campaignAssets)
      } else {
        setAssets([])
      }
    } catch (error) {
      console.error('Failed to load campaign assets:', error)
      setAssets([])
    } finally {
      setLoadingAssets(false)
    }
  }

  const loadUserData = async () => {
    try {
      // Load BRD rules from settings
      const brdResponse = await apiService.request('/api/user-data/brd-rules')
      if (brdResponse.success && brdResponse.data) {
        setBrdRules(brdResponse.data.rules || [])
      }

      // Load trafficking headers from settings  
      const traffickingResponse = await apiService.request('/api/settings/trafficking-headers')
      if (traffickingResponse.success && traffickingResponse.data) {
        setTraffickingHeaders(traffickingResponse.data.headers || [])
      }

      // Don't load global assets - we'll load campaign-specific assets instead
    } catch (error) {
      console.error('Failed to load user data:', error)
      // Load defaults for demo
      setBrdRules([
        { id: 1, rule: 'FDA Filing Number required', severity: 'critical', field: 'fdaFilingNumber' },
        { id: 2, rule: 'Customer Type must be specified', severity: 'warning', field: 'customerType' },
        { id: 3, rule: 'Indication required for HCP content', severity: 'warning', field: 'indication' }
      ])
      setTraffickingHeaders([
        'Country', 'Language', 'Advertiser ID', 'Creative Agency', 'Creative Status',
        'Creative Version', 'Customer Type', 'Campaign Type', 'Campaign Name',
        'Creative Name', 'Indication', 'Channel', 'CTA 1', 'URL 1', 'Creative Size',
        'FDA Filing Number', 'Processing Status', 'Export Date'
      ])
    }
  }

  const handleBRDUpload = async (file: File) => {
    try {
      const formData = new FormData()
      formData.append('brd', file)

      const response = await apiService.request('/api/brd/parse', {
        method: 'POST',
        body: formData
      })

      if (response.success && response.data) {
        setBrdRules(response.data.rules || [])
        
        // Save to user settings
        await apiService.request('/api/user-data/brd-rules', {
          method: 'POST',
          body: JSON.stringify({ rules: response.data.rules })
        })
        
        alert(`BRD processed successfully! Found ${response.data.rules?.length || 0} rules.`)
      } else {
        alert('Failed to process BRD file')
      }
    } catch (error) {
      console.error('BRD upload failed:', error)
      alert('Failed to upload BRD file')
    }
  }

  const handleTraffickingUpload = async (file: File) => {
    try {
      const formData = new FormData()
      formData.append('trafficking', file)

      const response = await apiService.request('/api/trafficking/parse', {
        method: 'POST',
        body: formData
      })

      if (response.success && response.data) {
        setTraffickingHeaders(response.data.headers || [])
        
        // Save to user settings
        await apiService.request('/api/settings/trafficking-headers', {
          method: 'POST',
          body: JSON.stringify({ headers: response.data.headers })
        })
        
        alert(`Trafficking matrix processed! Found ${response.data.headers?.length || 0} fields.`)
      } else {
        alert('Failed to process trafficking file')
      }
    } catch (error) {
      console.error('Trafficking upload failed:', error)
      alert('Failed to upload trafficking file')
    }
  }

  const handleAssetAdd = async (asset: any) => {
    try {
      setSaving(true)
      
      // Add asset data
      const assetWithMetadata = {
        ...asset,
        id: asset.id || `asset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        campaignId: campaignId || 'no-campaign',
        uploadedAt: new Date().toISOString(),
        status: 'processing'
      }
      
      // Update local state immediately
      setAssets(prev => {
        const existing = prev.find(a => a.id === assetWithMetadata.id)
        if (existing) {
          return prev.map(a => a.id === assetWithMetadata.id ? assetWithMetadata : a)
        } else {
          return [...prev, assetWithMetadata]
        }
      })

      // Save to campaign (not global assets)
      if (campaignId) {
        const result = await campaignService.addAssetToCampaign(campaignId, assetWithMetadata)
        if (result.success) {
          console.log('Asset added to campaign:', assetWithMetadata.id)
        }
      } else {
        // Fallback: save to global assets if no campaign
        await apiService.request('/api/assets', {
          method: 'POST',
          body: JSON.stringify({ asset: assetWithMetadata })
        })
      }
      
      setLastSaved(new Date())
    } catch (error) {
      console.error('Failed to save asset:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAndExit = async () => {
    if (!campaignId) {
      navigate('/campaigns')
      return
    }

    try {
      setSaving(true)
      
      // Update campaign status to draft and save metadata
      const updateData = {
        ...campaign,
        status: 'draft',
        assetCount: assets.length,
        lastModified: new Date().toISOString()
      }
      
      await apiService.request(`/api/campaigns/${campaignId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      })
      
      console.log('✅ Campaign saved as draft with', assets.length, 'assets')
      setLastSaved(new Date())
      
      // Small delay to show "Saving..." feedback
      setTimeout(() => {
        navigate('/campaigns')
      }, 300)
    } catch (error) {
      console.error('Failed to save campaign:', error)
      alert('Failed to save campaign. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const formatLastSaved = () => {
    if (!lastSaved) return 'Not saved yet'
    const seconds = Math.floor((new Date().getTime() - lastSaved.getTime()) / 1000)
    if (seconds < 10) return 'Just now'
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    return lastSaved.toLocaleTimeString()
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header with Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/campaigns')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Campaigns
            </Button>
            {campaign && (
              <div className="border-l border-gray-300 dark:border-gray-600 pl-4">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {campaign.name}
                </h1>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Save Status Indicator */}
            <div className="flex items-center gap-2 text-sm">
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-gray-600 dark:text-gray-400">Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Saved {formatLastSaved()}
                  </span>
                </>
              ) : null}
            </div>
            
            <Button
              onClick={handleSaveAndExit}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Draft
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Campaign Builder */}
      <CampaignBuilderComplete
        loadingAssets={loadingAssets}
        onBRDUpload={handleBRDUpload}
        onTraffickingUpload={handleTraffickingUpload}
        brdRules={brdRules}
        traffickingHeaders={traffickingHeaders}
        initialAssets={assets}
        onAssetAdd={handleAssetAdd}
        campaignName={campaign?.name}
        campaignDescription={campaign?.description}
      />
    </div>
  )
}
